/**
 * Cliente HTTP Axios configurado
 * OP-2026-0129: Frontend Admin SaaS
 *
 * IMPORTANTE (L017): X-Tenant-ID já está no CORS do backend (main.py:146)
 * IMPORTANTE (L018): VITE_API_URL em prod usa URL base do Load Balancer
 */

import axios from 'axios'

// Criar instância do Axios
const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
})

// Interceptor de Request - Adiciona token e tenant
apiClient.interceptors.request.use(
  (config) => {
    // Token de autenticação
    const token = localStorage.getItem('auth_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    // Tenant ID (X-Tenant-ID verificado no CORS - ERRO-005)
    const tenantId = localStorage.getItem('tenant_id')
    if (tenantId) {
      config.headers['X-Tenant-ID'] = tenantId
    }

    // Debug log em desenvolvimento
    if (import.meta.env.VITE_DEBUG === 'true') {
      console.log(`[API] ${config.method?.toUpperCase()} ${config.url}`)
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Interceptor de Response - Trata erros comuns
apiClient.interceptors.response.use(
  (response) => {
    return response
  },
  (error) => {
    const { response } = error

    if (response) {
      switch (response.status) {
        case 401:
          // Token expirado ou inválido - redireciona para login
          localStorage.removeItem('auth_token')
          localStorage.removeItem('tenant_id')
          // Usar BASE_URL para path correto (/admin/ em prod)
          window.location.href = import.meta.env.BASE_URL + 'login'
          break

        case 403:
          // Sem permissão
          console.error('[API] Acesso negado:', response.data?.detail)
          break

        case 404:
          // Recurso não encontrado
          console.error('[API] Recurso não encontrado:', response.config?.url)
          break

        case 400:
          // Requisição recusada por regra do backend (vínculo inválido etc.)
          // OP-20260921-001 (A.3): TELEMETRIA apenas. Quem decide texto e campo
          // é a store (interpretarErroApi); este interceptor não reescreve nada.
          console.error('[API] Requisição recusada (400):', response.data?.detail)
          break

        case 409:
          // Conflito de integridade - telemetria apenas (ver 400)
          console.error('[API] Conflito (409):', response.data?.detail)
          break

        case 422:
          // Erro de validação
          console.error('[API] Erro de validação:', response.data?.detail)
          break

        case 500:
        case 502:
        case 503:
          // Erro do servidor
          console.error('[API] Erro do servidor:', response.status)
          break

        default:
          console.error('[API] Erro:', response.status, response.data)
      }
    } else if (error.request) {
      // Requisição feita mas sem resposta (network error)
      console.error('[API] Erro de rede - sem resposta do servidor')
    } else {
      // Erro na configuração da requisição
      console.error('[API] Erro de configuração:', error.message)
    }

    return Promise.reject(error)
  }
)

export default apiClient

// Funções auxiliares para API
export const api = {
  get: (url, config = {}) => apiClient.get(url, config),
  post: (url, data, config = {}) => apiClient.post(url, data, config),
  put: (url, data, config = {}) => apiClient.put(url, data, config),
  patch: (url, data, config = {}) => apiClient.patch(url, data, config),
  delete: (url, config = {}) => apiClient.delete(url, config)
}
